import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, MessageCircle, Clock, ShieldCheck, MapPin } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL_HREF, WHATSAPP_HREF, GBP_RATING, GBP_REVIEW_COUNT } from "@/lib/contact";
import { track } from "@/lib/analytics";
import { lineWrap, lineChild, EASE_OUT_EXPO } from "@/lib/motion";

const HERO_BG =
  "https://images.pexels.com/photos/17720190/pexels-photo-17720190.png?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1700";

export default function Hero() {
  const wrapRef = useRef(null);
  const reduce = useReducedMotion();

  // Scroll-linked parallax on the hero image + a subtle title lift.
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-12%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.85]);

  // Pointer-follow spotlight (Awwwards signature moment).
  const onPointerMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty("--mx", `${x}%`);
    el.style.setProperty("--my", `${y}%`);
  };

  return (
    <section
      id="top"
      ref={wrapRef}
      onPointerMove={onPointerMove}
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] flex items-end md:items-center overflow-hidden bg-navy grain"
    >
      {/* Parallax image with cinema clip */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-20 will-change-transform"
      >
        <img
          src={HERO_BG}
          alt="Carro attrezzi Roma H24 in intervento notturno per soccorso stradale"
          width="1700"
          height="1100"
          className="h-full w-full object-cover object-center cinema-clip opacity-90"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </motion.div>

      {/* Navy gradient veil */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/70 to-navy/40"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy/95 via-navy/30 to-navy/60" />
      <div className="absolute inset-0 -z-10 hero-grid opacity-20" />
      {/* Signature spotlight follows the pointer */}
      <div className="absolute inset-0 -z-10 pointer-events-none spotlight mix-blend-screen" />

      {/* Editorial vertical rail (index / mark) */}
      <div className="pointer-events-none absolute left-4 top-24 md:left-8 md:top-1/2 md:-translate-y-1/2 z-10 hidden sm:flex flex-col items-start gap-3 text-white/60">
        <span className="font-mono text-[11px] tracking-[0.35em]">IDX / 001</span>
        <span className="h-16 w-px bg-white/25" />
        <span className="font-mono text-[11px] tracking-[0.35em]">ROMA · H24</span>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-28 md:pt-32 pb-16 md:pb-24 w-full relative z-10">
        <motion.div style={{ y: titleY }} className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7, ease: EASE_OUT_EXPO }}
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 rounded-full bg-white/8 ring-1 ring-white/20 px-3 py-1.5 text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-white/90 backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-brand-orange animate-ping opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-orange" />
            </span>
            Operativi adesso · Roma & Provincia
          </motion.span>

          <motion.h1
            data-testid="hero-title"
            initial="hidden"
            animate="visible"
            variants={lineWrap}
            className="mt-6 font-display font-black text-white text-shadow-strong text-[13vw] sm:text-7xl lg:text-[7.5rem] leading-[0.94] tracking-[-0.02em]"
          >
            <span className="line-mask">
              <motion.span variants={lineChild} className="block">
                Soccorso
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span variants={lineChild} className="block">
                stradale
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span
                variants={lineChild}
                className="block text-brand-orange italic font-black"
                style={{ fontFeatureSettings: '"ss01" on' }}
              >
                Roma H24.
              </motion.span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: EASE_OUT_EXPO }}
            data-testid="hero-subtitle"
            className="mt-6 text-white/85 text-base sm:text-xl max-w-2xl leading-relaxed"
          >
            <span className="font-bold text-white">Carroattrezzi Roma H24</span> —
            intervento rapido in tutta Roma e provincia.
            <span className="font-bold text-white"> 24 ore su 24, 7 giorni su 7</span>.
            Chiama ora, rispondiamo in pochi secondi.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: EASE_OUT_EXPO }}
            className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href={PHONE_TEL_HREF}
              onClick={() => track.phoneCall("hero")}
              data-testid="hero-call-button"
              className="group inline-flex items-center justify-center gap-3 rounded-2xl bg-brand-orange text-white font-bold px-6 py-4 text-lg sm:text-xl animate-pulse-ring hover:brightness-110 hover:-translate-y-0.5 transition btn-glow-orange"
            >
              <Phone className="h-6 w-6" />
              <span>Chiama Ora</span>
              <span className="font-mono">{PHONE_DISPLAY}</span>
            </a>
            <a
              href={WHATSAPP_HREF}
              onClick={() => track.whatsapp("hero")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center justify-center gap-3 rounded-2xl bg-whatsapp text-white font-bold px-6 py-4 text-lg sm:text-xl hover:brightness-110 hover:-translate-y-0.5 transition btn-glow-green"
            >
              <MessageCircle className="h-6 w-6" />
              WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: EASE_OUT_EXPO }}
            className="mt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl"
          >
            <Stat icon={<Clock className="h-4 w-4" />} label="Operativi" value="24/7" />
            <Stat icon={<MapPin className="h-4 w-4" />} label="Copertura" value="Roma & Prov." />
            <Stat icon={<ShieldCheck className="h-4 w-4" />} label="Google" value={`${String(GBP_RATING).replace(".", ",")} / ${GBP_REVIEW_COUNT}`} />
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.9 }}
        className="pointer-events-none absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/60"
      >
        <span className="font-mono text-[10px] tracking-[0.4em] uppercase">Scorri</span>
        <span className="relative h-10 w-px overflow-hidden bg-white/20">
          <span className="absolute inset-x-0 top-0 h-3 w-px bg-brand-orange animate-[scrollcue_1.8s_ease-in-out_infinite]" />
        </span>
      </motion.div>

      <style>{`
        @keyframes scrollcue {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(220%); }
        }
      `}</style>
    </section>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/8 backdrop-blur ring-1 ring-white/15 p-3 sm:p-4">
      <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-wider font-semibold">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display font-extrabold text-white text-lg sm:text-2xl">{value}</div>
    </div>
  );
}
